/**
 * Shared service icon extraction for workflow/operator cards.
 * Used by My Operators and Marketplace to show integration icons.
 */

import { getJSON } from "./storage.client";

export type ServiceIcon = { iconUrl: string; serviceName: string };

export const BUILT_IN_INTEGRATION_ICONS: Record<string, string> = {
  call_ai_llm: "/img/integrations/llm.svg",
  generate_ai_image: "/img/integrations/image_generation.svg",
  web_search: "/img/integrations/streamline-color_web-flat.svg",
  excel: "/img/integrations/fa-solid_desktop.svg",
  word: "/img/integrations/doc.svg",
  powerpoint: "/img/integrations/powerpoint.svg",
  pdf: "/img/integrations/bxs_file-pdf.svg",
  llm: "/img/integrations/llm.svg",
  image_generation: "/img/integrations/image_generation.svg",
  human_input_form: "/img/integrations/tabler_message-2-filled.svg",
  tts: "/img/integrations/llm.svg",
  output_artifacts: "/img/integrations/fa-solid_desktop.svg",
};

/** Workflow-like shape with optional services (top-level or under workflow) */
type WorkflowLike = {
  services?: string[];
  workflow?: { services?: string[] };
};

/**
 * Extract service icons from workflow data (client-only; uses localStorage cache).
 */
export function extractServiceIcons(
  workflow: WorkflowLike | null | undefined,
): ServiceIcon[] {
  const icons: ServiceIcon[] = [];

  if (typeof window === "undefined") {
    return icons;
  }

  try {
    const normalize = (v: string) =>
      v
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "_");

    // Whole segments only. A plain `includes` matched a key that merely appeared
    // somewhere in the name: "wordpress" contains "word" and anything with
    // "password" in it does too, so both resolved to the Microsoft Word icon --
    // and because a built-in hit `continue`s past the integration index below,
    // the wrong icon won permanently. `normalize` has already turned every
    // separator into `_`, so wrapping both sides makes the test boundary-aware
    // while still matching multi-segment keys like `web_search`.
    const matchesServiceSegment = (value: string, key: string) =>
      `_${value}_`.includes(`_${key}_`);

    const parsed = getJSON<{
      actions: Record<string, { iconUrl: string | null; brandColor?: string }>;
      integrations: Record<
        string,
        { iconUrl: string | null; brandColor?: string }
      >;
    }>("sv_integrations_index", { namespace: "sv_cache" });

    const allServices = new Set<string>();

    if (workflow && Array.isArray(workflow.services)) {
      for (const service of workflow.services) {
        if (service && typeof service === "string") allServices.add(service);
      }
    }
    if (
      allServices.size === 0 &&
      workflow?.workflow &&
      Array.isArray(workflow.workflow.services)
    ) {
      for (const service of workflow.workflow.services) {
        if (service && typeof service === "string") allServices.add(service);
      }
    }

    if (allServices.size === 0) return icons;

    const formatServiceName = (service: string): string =>
      service
        .split("/")
        .map((part) =>
          part
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        )
        .join(" / ");

    for (const service of Array.from(allServices).slice(0, 5)) {
      if (icons.length >= 5) break;

      const serviceParts = service.split("/");
      const integrationName = serviceParts[0] || service;
      const actionName = serviceParts[1] || null;

      const normalizedService = normalize(service);
      const normalizedIntegration = normalize(integrationName);
      const normalizedAction = actionName ? normalize(actionName) : null;
      const displayName = formatServiceName(service);

      const builtInIcon =
        BUILT_IN_INTEGRATION_ICONS[normalizedService] ||
        BUILT_IN_INTEGRATION_ICONS[normalizedIntegration] ||
        Object.entries(BUILT_IN_INTEGRATION_ICONS).find(
          ([key]) =>
            matchesServiceSegment(normalizedService, key) ||
            matchesServiceSegment(normalizedIntegration, key),
        )?.[1];
      if (builtInIcon) {
        icons.push({ iconUrl: builtInIcon, serviceName: displayName });
        continue;
      }

      if (parsed) {
        let hit =
          parsed.actions[normalizedService] ||
          parsed.integrations[normalizedService];
        if (!hit) hit = parsed.integrations[normalizedIntegration];
        if (!hit && normalizedAction) hit = parsed.actions[normalizedAction];
        if (!hit && normalizedAction) {
          const combinedKey = `${normalizedIntegration}_${normalizedAction}`;
          hit = parsed.actions[combinedKey] || parsed.integrations[combinedKey];
        }
        if (!hit) {
          const noUnderscore = normalizedService.replaceAll("_", "");
          hit =
            parsed.actions[noUnderscore] || parsed.integrations[noUnderscore];
        }
        if (hit?.iconUrl) {
          icons.push({ iconUrl: hit.iconUrl, serviceName: displayName });
        }
      }
    }
  } catch {
    // Silently fail
  }

  return icons;
}
